import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { calcDoc, fmt, genNum, today } from '../../utils/calc';
import '../modals/Modal.css';

function useFedaPay() {
  const [ready, setReady] = useState(!!window.FedaPay);
  useEffect(() => {
    if (window.FedaPay) { setReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://cdn.fedapay.com/checkout.js?v=1.1.7';
    s.async = true;
    s.onload  = () => setReady(true);
    s.onerror = () => console.error('FedaPay SDK non chargé');
    document.body.appendChild(s);
  }, []);
  return ready;
}

export default function PaiementSurDevis({ onClose }) {
  const { data, saveTransaction, updateTransaction, showToast } = useApp();
  const fedaReady = useFedaPay();

  const prestations = [
    ...data.domaines.map(d => d.name),
    ...(data.produits || []).filter(p => p.active !== false).map(p => 'Application: ' + p.name),
    ...(data.formations || []).map(f => 'Formation: ' + f.title),
    'Application sur mesure',
    'Autre / Prestation sur devis',
  ];

  const [prestation, setPrestation] = useState('');
  const [refDevis, setRefDevis] = useState('');
  const [cnom, setCnom] = useState('');
  const [ctel, setCtel] = useState('');
  const [cemail, setCemail] = useState('');
  const [montant, setMontant] = useState('');
  const [savedId, setSavedId] = useState(null);
  const [payStatus, setPayStatus] = useState(null); // null | 'loading' | 'success' | 'error'

  async function ensureSaved() {
    if (savedId) return savedId;
    const num = genNum('DEV-PAY');
    const lines = [{ desc: `${prestation}${refDevis ? ' (réf. devis ' + refDevis + ')' : ''}`, qty: 1, unit: 'forfait', prix: Number(montant) }];
    const totals = calcDoc(lines, 0, 0, 0);
    const id = await saveTransaction(
      'paiement_devis', lines,
      { num, date: today(), valid: 0, cnom, ctel, cemail, cadr: '', refDevis, prestation,
        notes: 'Paiement en ligne pour prestation sur devis / sur mesure.' },
      totals
    );
    setSavedId(id);
    return id;
  }

  async function handlePay() {
    if (!prestation) { showToast('Sélectionnez la prestation concernée.', 'warn'); return; }
    if (!cnom || !ctel) { showToast('Renseignez votre nom et téléphone.', 'warn'); return; }
    const amt = Number(montant);
    if (!amt || amt <= 0) { showToast('Indiquez le montant exact reçu sur votre devis.', 'warn'); return; }
    if (!fedaReady || !window.FedaPay) { showToast('SDK FedaPay non chargé', 'error'); return; }

    const txId = await ensureSaved();
    setPayStatus('loading');

    window.FedaPay.init({
      public_key: process.env.REACT_APP_FEDAPAY_PUBLIC_KEY || 'pk_live_5ZFG7_VsT85YMgYhpxSx4W04',
      transaction: {
        amount: Math.round(amt),
        description: `Paiement ${prestation} – ${cnom}${refDevis ? ' – Devis ' + refDevis : ''}`,
      },
      customer: {
        firstname: cnom || 'Client', lastname: '', email: cemail || '',
        phone_number: { number: ctel?.replace(/\s/g, '') || '', country: 'BJ' },
      },
      currency: { iso: 'XOF' },
      onComplete: async function (resp) {
        if (resp.reason === window.FedaPay.DIALOG_DISMISSED) {
          setPayStatus(null);
          showToast('Paiement annulé', 'warn');
          return;
        }
        const tx = resp.transaction;
        if (tx?.status === 'approved') {
          setPayStatus('success');
          await updateTransaction(txId, {
            fedapay_status: 'approved',
            fedapay_tx_id: String(tx.id),
            fedapay_date: new Date().toLocaleDateString('fr-FR'),
            fedapay_mode: tx.payment_method || 'mobile_money',
            statut: 'payé',
          });
          showToast('✅ Paiement confirmé !', 'ok');
        } else {
          setPayStatus('error');
          showToast('❌ Paiement échoué ou refusé', 'error');
        }
      },
    }).open();
  }

  return (
    <div className="overlay open" style={{ zIndex: 9200 }}>
      <div className="modal modal-md">
        <div className="mhead">
          <h2>💳 Payer une prestation / un devis</h2>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>
        <div className="mbody">
          {payStatus === 'success' ? (
            <div style={{ textAlign: 'center', padding: '20px 10px' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <h3 style={{ color: 'var(--green)', marginBottom: 8 }}>Paiement confirmé !</h3>
              <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.6 }}>
                Merci {cnom} ! Votre paiement pour « {prestation} » a bien été enregistré.
                Notre équipe vous contacte sous peu au {ctel}.
              </p>
              <button className="abtn abtn-blue" style={{ marginTop: 18 }} onClick={onClose}>Fermer</button>
            </div>
          ) : (
            <>
              <p className="tab-desc" style={{ marginTop: 0 }}>
                Vous avez reçu un devis, une facture, ou souhaitez régler une prestation sur mesure ?
                Sélectionnez-la ci-dessous et indiquez le montant exact reçu pour payer en ligne, en toute sécurité.
              </p>

              <div className="fg2">
                <label>Prestation concernée *</label>
                <select value={prestation} onChange={e => setPrestation(e.target.value)}>
                  <option value="">Sélectionner...</option>
                  {prestations.map((s, i) => <option key={i} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid2">
                <div className="fg2"><label>Référence du devis/facture (optionnel)</label>
                  <input value={refDevis} onChange={e => setRefDevis(e.target.value)} placeholder="Ex: DEV-2026-045" />
                </div>
                <div className="fg2"><label>Montant à payer (FCFA) *</label>
                  <input type="number" value={montant} onChange={e => setMontant(e.target.value)} placeholder="Ex: 150000" />
                </div>
              </div>

              <h3 className="section-sep">Vos coordonnées</h3>
              <div className="grid2">
                <div className="fg2"><label>Nom complet *</label>
                  <input value={cnom} onChange={e => setCnom(e.target.value)} placeholder="Votre nom" />
                </div>
                <div className="fg2"><label>Téléphone *</label>
                  <input value={ctel} onChange={e => setCtel(e.target.value)} placeholder="+229 XX XX XX XX" />
                </div>
              </div>
              <div className="fg2"><label>Email (reçu de paiement)</label>
                <input type="email" value={cemail} onChange={e => setCemail(e.target.value)} placeholder="vous@email.com" />
              </div>

              {payStatus === 'error' && (
                <div style={{ background: 'rgba(255,64,96,0.15)', border: '1px solid #FF4060', borderRadius: 8, padding: '10px 14px', margin: '10px 0', color: '#FF4060', fontWeight: 700, fontSize: 13 }}>
                  ❌ Paiement échoué. Veuillez réessayer.
                </div>
              )}

              <button
                className="abtn abtn-green"
                style={{ width: '100%', justifyContent: 'center', fontSize: 14, padding: '12px', marginTop: 12 }}
                onClick={handlePay}
                disabled={payStatus === 'loading'}
              >
                {payStatus === 'loading' ? '⏳ Traitement en cours...' : montant ? `💳 Payer ${fmt(Number(montant) || 0)}` : '💳 Payer'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
